import React, { useState } from 'react';

import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import {
  ACTION_ACKNOWLEDGE,
  ACTION_RESOLVE,
  issue_action_payload,
  reason_error
} from '../needs_attention/params';
import { NoteField } from './dialog_parts';
import { KIND, error_view } from './error_handlers';
import { PILL, pill_view } from './adapters';

/*
 * PROD-7828 R6 — the session page's payment-review pill (D90): the session's issue-log rows
 * (GET /penalties/session issues[]) with Acknowledge / Resolve through the EXISTING
 * POST /needs_attention/issue_action; no new write path. Hidden with no issue, "N to review" while any
 * is open, "Reviewed" once all are decided. Props-only: the session page (views/classes) mounts it;
 * teacher_names (uuid -> name) labels each issue when the session has several teachers;
 * post_action(payload) posts the issue action, on_changed() re-reads the session.
 */
// CD-62: an unanswered issue action may or may not have landed; the rows are re-read and a retry is safe.
export const PILL_UNCERTAIN_TEXT =
  'The connection dropped; the issue may or may not have been updated. The list is refreshed; trying again is safe.';

const ReviewPill = ({ issues, teacher_names, post_action, on_changed, on_permission }) => {
  const view = pill_view(issues, teacher_names);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  if (view.state === PILL.HIDDEN) return null;
  // The same reason rule as the Needs attention tab: the endpoint is the same one.
  const reason_err = reason_error(reason);

  const act = (issue, action) => {
    setTouched(true);
    if (reason_err || busy) return;
    setBusy(true);
    setMessage(null);
    post_action(issue_action_payload([issue.id], action, reason))
      .then((data) => {
        const res = ((data && data.results) || []).find(
          (r) => Number(r.issue_id) === Number(issue.id)
        );
        setMessage(
          res && res.applied === false
            ? { tone: 'error', text: res.message || 'This issue could not be updated.' }
            : {
                tone: 'ok',
                text: `${issue.label}: ${action === ACTION_RESOLVE ? 'resolved' : 'acknowledged'}.`
              }
        );
        on_changed();
      })
      .catch((e) => {
        const err = error_view(e, { write: true });
        if (err.kind === KIND.PERMISSION) {
          on_permission(e);
          return;
        }
        setMessage({
          tone: 'error',
          text: err.kind === KIND.UNCERTAIN ? PILL_UNCERTAIN_TEXT : err.text
        });
        on_changed();
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        type="button"
        className="tp-pe-pill"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <StatusPill tone={view.state === PILL.TO_REVIEW ? 'orange' : 'green'}>
          Payment review: {view.text}
        </StatusPill>
      </button>
      {open ? (
        <TpDialog
          title_id="tp-pe-pill-title"
          busy={busy}
          guard_text={discard_guard_text(reason)}
          on_close={() => setOpen(false)}
        >
          <div className="tp-modal">
            <h4 id="tp-pe-pill-title">Payment review for this session</h4>
            <ul className="tp-pe-issues">
              {view.issues.map((i) => (
                <li key={i.id}>
                  <span>
                    {i.label} · {i.status_text}
                  </span>
                  <span className="tp-actions">
                    {i.can_acknowledge ? (
                      <button
                        type="button"
                        className="tp-link"
                        onClick={busy ? undefined : () => act(i, ACTION_ACKNOWLEDGE)}
                        aria-disabled={busy ? 'true' : undefined}
                      >
                        Acknowledge
                      </button>
                    ) : null}
                    {i.can_resolve ? (
                      <button
                        type="button"
                        className="tp-link"
                        onClick={busy ? undefined : () => act(i, ACTION_RESOLVE)}
                        aria-disabled={busy ? 'true' : undefined}
                      >
                        Resolve
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
            <NoteField
              id="tp-pe-pill-reason"
              label="Reason (internal, required)"
              value={reason}
              on_change={setReason}
              error_text={touched ? reason_err : ''}
            />
            {message ? (
              <div
                className={`tp-banner ${message.tone === 'ok' ? 'tp-banner-ok' : 'tp-banner-error'}`}
                role={message.tone === 'ok' ? 'status' : 'alert'}
              >
                {message.text}
              </div>
            ) : null}
            <div className="tp-modal-ft">
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={busy ? undefined : () => setOpen(false)}
                aria-disabled={busy ? 'true' : undefined}
              >
                Close
              </button>
            </div>
          </div>
        </TpDialog>
      ) : null}
    </>
  );
};

export default ReviewPill;
